import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { TokenService } from '@services/rest/token.service';
import { SessionService } from '@services/rest/session.service';
import { PresenceService } from '@services/rest/presence.service';

// Requests to these endpoints must never trigger a silent-refresh retry —
// doing so on /Refresh itself would recurse forever, and the others simply
// don't carry a session yet.
const AUTH_ENDPOINTS = ['/Login', '/RunnerLogin', '/Refresh', '/Logout'];

// Background presence ping (see PresenceService) — the user never initiated
// it, so a failure toast would just confuse them. Session expiry (401) is
// still handled normally below since that's a meaningful, expected flow.
const HEARTBEAT_ENDPOINT = '/Heartbeat';

@Injectable()
export class ApiErrorInterceptor implements HttpInterceptor {
    constructor(
        private notification: NzNotificationService,
        private router: Router,
        private spinner: NgxSpinnerService,
        private tokenService: TokenService,
        private sessionService: SessionService,
        private presenceService: PresenceService) {
    }

    private isAuthEndpoint(url: string): boolean {
        return AUTH_ENDPOINTS.some(endpoint => url.endsWith(endpoint));
    }

    private redirectToLogin() {
        this.tokenService.clearSession();
        this.sessionService.stop();
        this.presenceService.stop();

        const currentUrl = this.router.url || "/";
        const currentPath = currentUrl.split('?')[0].split('#')[0];
        // Don't redirect when already on an auth page ('/', '/login',
        // error pages) — otherwise the auth URL itself becomes the
        // returnUrl and the user gets bounced back to /login (or the
        // root login page) after signing in.
        const onAuthPage = currentPath === '' || currentPath === '/'
            || currentPath.startsWith('/login')
            || currentPath.startsWith('/404')
            || currentPath.startsWith('/500');
        if (!onAuthPage) {
            this.notification.error("Error", "Session Expired");
            this.router.navigate(["/login"], { queryParams: { returnUrl: currentUrl } });
        }
    }

    private handleNonAuthError(error: HttpErrorResponse) {
        this.spinner.hide();

        if (error.status == 400) {
            const message = typeof error?.error === 'string' && error.error !== ''
                ? error.error
                : error?.error?.errorMessage ?? "Please try again later.";
            this.notification.error("Error", message);
        } else {
            this.notification.error("Error", error?.error?.errorMessage ?? "Please try again later.");
        }

        return throwError(() => error);
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status !== 401) {
                    if (request.url.endsWith(HEARTBEAT_ENDPOINT)) {
                        this.spinner.hide();
                        return throwError(() => error);
                    }
                    return this.handleNonAuthError(error);
                }

                this.spinner.hide();

                if (this.isAuthEndpoint(request.url)) {
                    this.redirectToLogin();
                    return throwError(() => error);
                }

                // Access token expired mid-request: silently refresh and
                // retry once before giving up and forcing a re-login.
                return this.sessionService.refresh().pipe(
                    switchMap(() => {
                        const retriedRequest = request.clone({
                            headers: request.headers.set('Authorization', `Bearer ${this.tokenService.getAccessToken()}`)
                        });
                        return next.handle(retriedRequest);
                    }),
                    catchError(() => {
                        this.redirectToLogin();
                        return throwError(() => error);
                    })
                );
            })
        );
    }
}
